import { Modal, Input } from '../components/ui';
import { apiErrorMessage } from '../lib/api-error-message';
/**
 * T144 — /expenses/:id detail page (US1 MVP).
 * T490 — click-to-edit on invoice_number / invoice_date / Kategorie /
 *        Status. Saves go through the same endpoints the
 *        Durcharbeitemodus uses, so revisions land identically.
 *
 * `/expenses/$id_/edit` stays for full bulk-edit (totals + items).
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { Button, GlassPanel, LoadingSpinner, PageHeader } from '../components/ui';
import {
  useExpense,
  useTransitionExpenseStatus,
  useAssignCategory,
} from '../services/api/expenses';
import { useExpenseAttachments } from '../services/api/attachments';
import { useExpenseComments, useCreateExpenseComment } from '../services/api/comments';
import { NachträglichWarning } from '../components/expenses/NachträglichWarning';
import { ExpenseAttachmentsList } from '../components/expenses/ExpenseAttachmentsList';
import { ExpenseRemovalAction } from '../components/expenses/ExpenseRemovalAction';
import { ErechnungMismatchWarning } from '../components/expenses/ErechnungMismatchWarning';
import { GiroCodeMismatchBanner } from '../components/expenses/GiroCodeMismatchBanner';
import { ExpenseAttachmentMultiPagePreview } from '../components/expenses/ExpenseAttachmentMultiPagePreview';
import { OcrFailureBanner } from '../components/expenses/OcrFailureBanner';
import { getOcrFailureSeverity } from '../components/expenses/-ocrFailureSeverity';
import { OcrPipelineProgress } from '../components/expenses/OcrPipelineProgress';
import { CategoryComboboxWithCreate } from '../components/categories/CategoryComboboxWithCreate';
import { useProcessingStatus } from '../services/api/upload';
import { formatDateBerlin, formatDateTimeBerlin } from '../lib/format-date';
import { useToastHelpers } from '../components/ui/Toast';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { expenseKeys, expenseAttachmentKeys } from '../services/api/keys';
import { useActiveTenantId, useSession } from '../hooks/useTenantContext';
import { formatCurrency, formatNumber, type ExpenseStatusInput } from '@armard/shared';

export const Route = createFileRoute('/expenses_/$id')({
  component: ExpenseDetailPage,
});

function ExpenseDetailPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <ExpenseDetailInner />
      </AppShell>
    </ProtectedRoute>
  );
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Entwurf',
  neu: 'Neu',
  geprüft: 'Geprüft',
  rückfrage_offen: 'Rückfrage',
  nicht_relevant: 'Nicht relevant',
  zurückgezogen: 'Zurückgezogen',
  archiviert: 'Archiviert',
};

// T493 — Quelle/source metadata for the Herkunft section. Same labels as
// the list-page Quelle column, kept inlined here so the detail page is
// self-contained.
const SOURCE_LABELS: Record<string, { icon: string; label: string; hint: string }> = {
  manual: { icon: '✋', label: 'Manuell', hint: 'Vom User direkt im CRM erfasst' },
  email: { icon: '📧', label: 'E-Mail', hint: 'Automatisch via IMAP-Postfach eingelesen' },
  drive: { icon: '💾', label: 'Google Drive', hint: 'Automatisch via Drive-Watcher eingelesen' },
  erechnung: {
    icon: '📄',
    label: 'eRechnung',
    hint: 'Strukturierte XML/PDF eRechnung (ZUGFeRD/XRechnung)',
  },
  ki: { icon: '🤖', label: 'KI', hint: 'Vorverarbeitet durch KI-Provider' },
};

const NEXT_STATUS_PER_STATUS: Record<string, ExpenseStatusInput[]> = {
  draft: ['neu'],
  neu: ['geprüft', 'rückfrage_offen', 'nicht_relevant', 'zurückgezogen'],
  geprüft: ['rückfrage_offen', 'nicht_relevant', 'zurückgezogen'],
  rückfrage_offen: ['geprüft', 'nicht_relevant', 'zurückgezogen'],
  nicht_relevant: ['neu', 'zurückgezogen'],
  zurückgezogen: [],
  archiviert: [],
};

// T491: localStorage key for the preview/edit-panel layout preference.
// Persists power-user's "I want the edit panel expanded by default" choice
// across navigations.
const EDIT_PANEL_KEY = 'expense-detail-edit-expanded';

function hydrateEditExpanded(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(EDIT_PANEL_KEY) === '1';
  } catch {
    return false;
  }
}

function ExpenseDetailInner() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const { data: session } = useSession();
  // T525: quick-search input state at the header. Enter triggers a
  // search-by-supplier_invoice_number; single match → jump to detail,
  // multi-match → land on the list with a pre-filtered q parameter.
  const [quickSearch, setQuickSearch] = useState('');
  const [pendingStatus, setPendingStatus] = useState<ExpenseStatusInput | null>(null);
  const [statusReason, setStatusReason] = useState('');
  const { data: expense, isLoading, error } = useExpense(id);
  // Spec 004 US5 — categories now loaded inside CategoryComboboxWithCreate;
  // the previous list fetch here became unused after the inline-picker
  // swap. Removed to keep the route slim.
  const transitionStatus = useTransitionExpenseStatus(id);
  const assignCategory = useAssignCategory(id);
  // T490 — inline updates (invoice_number / invoice_date) go through the
  // same useUpdateExpense the bulk-edit route uses, so the revision history
  // stays unified.

  // T492 — pull pipeline phase so the failure banner can react to a
  // crashed extraction. Polling stops once phase reaches done/failed.
  const { data: processingStatus } = useProcessingStatus(id);
  // T483/T491/T492 — attachments aren't enriched on the detail DTO; load
  // them via the dedicated `/expenses/:id/attachments` endpoint so the
  // preview component + failure-severity helper get the real list.
  const { data: attachmentsList } = useExpenseAttachments(id);
  const attachments = attachmentsList?.items ?? [];
  const [categoryPick, setCategoryPick] = useState('');
  // T491: edit-panel expand/collapse, persisted to localStorage.
  const [editExpanded, setEditExpanded] = useState<boolean>(hydrateEditExpanded);
  const toggleEditExpanded = () => {
    setEditExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(EDIT_PANEL_KEY, next ? '1' : '0');
      } catch {
        /* fall through — UI state still updates */
      }
      return next;
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) {
    return <div className="p-8 text-red-400">Fehler: {(error as Error).message}</div>;
  }
  if (!expense) return null;

  const roleTargets =
    session?.activeRole === 'geschäftsführer'
      ? ['neu']
      : session?.activeRole === 'steuerberater'
        ? ['geprüft', 'rückfrage_offen', 'nicht_relevant']
        : [];
  const allowedNext = (NEXT_STATUS_PER_STATUS[expense.status] ?? []).filter((status) =>
    roleTargets.includes(status)
  );
  const currentExpense = expense;

  async function onStatusChange(toStatus: ExpenseStatusInput) {
    if (toStatus === 'nicht_relevant' || toStatus === 'zurückgezogen') {
      setStatusReason('');
      setPendingStatus(toStatus);
      return;
    }
    await performStatusChange(toStatus, null);
  }
  async function performStatusChange(toStatus: ExpenseStatusInput, reason: string | null) {
    try {
      await transitionStatus.mutateAsync({
        toStatus,
        reason,
        version: currentExpense.version,
      });
      setPendingStatus(null);
      toast.success('Status aktualisiert');
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function onAssignCategory(idToAssign?: string) {
    const target = idToAssign ?? categoryPick;
    if (!target) return;
    try {
      await assignCategory.mutateAsync({
        categoryId: target,
        version: currentExpense.version,
      });
      toast.success('Kategorie zugewiesen');
      setCategoryPick('');
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  return (
    <div className="max-w-4xl space-y-6">
      <Modal
        isOpen={pendingStatus !== null}
        onClose={() => setPendingStatus(null)}
        title="Belegstatus ändern"
      >
        <p className="text-sm text-white/60 mb-4">
          {pendingStatus === 'zurückgezogen'
            ? 'Der Beleg wird zurückgezogen.'
            : 'Der Beleg wird als nicht relevant markiert.'}{' '}
          Bitte begründe diese Änderung.
        </p>
        <Input
          label="Begründung"
          value={statusReason}
          onChange={(e) => setStatusReason(e.target.value)}
        />
        <div className="flex flex-wrap justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={() => setPendingStatus(null)}>
            Abbrechen
          </Button>
          <Button
            disabled={!statusReason.trim() || transitionStatus.isPending}
            onClick={() =>
              pendingStatus && void performStatusChange(pendingStatus, statusReason.trim())
            }
          >
            Status bestätigen
          </Button>
        </div>
      </Modal>
      <PageHeader
        backTo="/expenses"
        backLabel="Ausgaben"
        title={`Beleg ${expense.expenseNumber ?? expense.id.slice(0, 8)}`}
        subtitle={
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span>{expense.supplierName ?? '— Lieferant fehlt —'}</span>
            <span className="text-white/30">·</span>
            <span className="text-white/50 text-xs uppercase tracking-wider">Belegnr.</span>
            <span>{expense.supplierInvoiceNumber || 'Fehlt'}</span>
            <span className="text-white/30">·</span>
            <span>
              {expense.invoiceDate ? formatDateBerlin(expense.invoiceDate) : 'Datum fehlt'}
            </span>
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ExpenseRemovalAction id={id} status={expense.status} version={expense.version} />
            {expense.status !== 'archiviert' && (
              <Link to="/expenses/$id/edit" params={{ id }}>
                <Button variant="secondary">Bearbeiten</Button>
              </Link>
            )}
            {/* T525: Belegnummer-Quick-Search. Enter triggers a list query
                by supplier_invoice_number; single hit → jump to detail,
                multi-hit or no-hit → land on the list with the search
                term pre-filled in `q`. */}
            <input
              type="search"
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key !== 'Enter' || !quickSearch.trim()) return;
                e.preventDefault();
                const term = quickSearch.trim();
                try {
                  // The list endpoint supports an ILIKE `q` filter that
                  // matches `supplier_invoice_number` (+ customer_ref + notes).
                  // Single hit → jump straight to detail; ambiguous → land
                  // on the list with the term pre-filled so the user can
                  // narrow further.
                  const res = await fetch(
                    `/api/v1/expenses?q=${encodeURIComponent(term)}&limit=5`,
                    { credentials: 'include' }
                  );
                  if (res.ok) {
                    const data = (await res.json()) as {
                      items: Array<{ id: string; supplierInvoiceNumber: string | null }>;
                    };
                    const exact = data.items.find((e) => e.supplierInvoiceNumber === term);
                    if (exact) {
                      void navigate({ to: '/expenses/$id', params: { id: exact.id } });
                      return;
                    }
                    if (data.items.length === 1) {
                      void navigate({ to: '/expenses/$id', params: { id: data.items[0].id } });
                      return;
                    }
                  }
                } catch {
                  /* fall through to list-with-pre-filter */
                }
                void navigate({
                  to: '/expenses',
                  search: { q: term, page: 1 } as Record<string, unknown>,
                });
              }}
              placeholder="Belegnr. → Enter"
              aria-label="Belegnummer-Quick-Search"
              data-testid="detail-quick-search"
              className="bg-white/5 border border-white/10 rounded px-3 py-1.5 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-white/20"
            />
            {/* T529: OCR-Debug-Modus Link — surfaced for gf + stbr only,
                which is also the backend gate. UI-side we just render the
                link for all roles; an admin/observer wouldn't have access
                to the detail page in the first place. The route tree is
                regenerated by the Vite plugin at dev-start, so the
                typecheck-time cast is just a build-time workaround. */}
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <a
              href={`/expenses/${expense.id}/ocr-debug`}
              data-testid="detail-ocr-debug-link"
              className="inline-block"
            >
              <Button variant="ghost">🔍 OCR-Debug</Button>
            </a>
            <Link to="/expenses">
              <Button variant="ghost">Zurück</Button>
            </Link>
          </div>
        }
      />

      {(expense as { nachträglich?: boolean }).nachträglich && <NachträglichWarning />}

      {expense.erechnungAmountMismatch && (
        <ErechnungMismatchWarning mismatch={expense.erechnungAmountMismatch} />
      )}

      {/* T569 (FR-013-MIS) — 3-Way Mismatch blocker banner. Renders
          only when expense.girocodeAmountMismatch is true; click-
          handler POSTs to /ocr-debug/resolve-mismatch (T552 backend)
          with the user-chosen field + source + value. */}
      {expense.girocodeAmountMismatch && (
        <GiroCodeMismatchBanner expenseId={id} mismatch={expense.girocodeAmountMismatch} />
      )}

      {/* T492 — OCR-Failure-Banner. Pure helper decides severity from the
          pipeline phase + trustLevel + attachment confidences. */}
      {(() => {
        const severity = getOcrFailureSeverity({
          processingPhase: processingStatus?.phase ?? null,
          trustLevel: expense.trustLevel,
          source: expense.source,
          attachments: attachments as ReadonlyArray<{
            isPrimary?: boolean;
            ocrFieldConfidences?: Record<string, number> | null;
          }>,
        });
        return severity ? (
          <OcrFailureBanner severity={severity} errorMessage={processingStatus?.errorMessage} />
        ) : null;
      })()}

      {/* Spec 004 US3 (T020) — per-layer OCR-pipeline progress with
          liveness indicator. Shares the `processingStatus` poll above
          (TanStack Query dedup; no extra fetch). LLM-Fallback layer
          stays idle in 004 per JJ HITL 2026-05-18 ("Degrade") —
          audit-reader projection follow-up tracked as
          `T-cdx-audit-projection`. */}
      <OcrPipelineProgress
        layersInput={{
          processingPhase: processingStatus?.phase ?? null,
          errorMessage: processingStatus?.errorMessage ?? null,
          source: expense.source,
          llmAuditEvent: null,
        }}
      />

      {/* T493 + T526 — Herkunft section: source badge + createdAt + uploader
          (when known). Per-source metadata (mail-subject, drive-path) needs
          ingress columns the schema doesn't have yet — deferred. */}
      {(() => {
        const meta = SOURCE_LABELS[expense.source] ?? {
          icon: '·',
          label: expense.source,
          hint: '',
        };
        const primary = attachments.find((a) => a.isPrimary) ?? attachments[0];
        const uploaderName = (primary as { uploadedByUserName?: string | null } | undefined)
          ?.uploadedByUserName;
        return (
          <div
            className="flex flex-wrap items-center gap-3 text-sm text-white/70 bg-white/5 border border-white/10 rounded-lg px-4 py-2"
            data-testid="detail-herkunft-section"
          >
            <span className="text-white/50 text-xs uppercase tracking-wider">Herkunft</span>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/5 border border-white/10"
              data-testid="detail-source-badge"
              title={meta.hint}
            >
              <span aria-hidden="true">{meta.icon}</span>
              <span>{meta.label}</span>
            </span>
            <span className="text-white/40">·</span>
            <span>
              Erstellt am{' '}
              <span className="text-white/80" data-testid="detail-created-at">
                {formatDateTimeBerlin(expense.createdAt)}
              </span>
            </span>
            {uploaderName && (
              <>
                <span className="text-white/40">·</span>
                <span>
                  Hochgeladen von{' '}
                  <span className="text-white/80" data-testid="detail-uploader-name">
                    {uploaderName}
                  </span>
                </span>
              </>
            )}
          </div>
        );
      })()}

      {/* T491 — Preview becomes the prominent hero element (60vh, expands to
          full height when the edit panel collapses). T483-component, sized
          per the edit-panel state.
          T557 (FR-001b): multi-page wrapper hides the page-pagination bar
          when there's only one attachment, so 1-attachment belegs render
          identically to the pre-T557 behaviour. */}
      <ExpenseAttachmentMultiPagePreview
        attachments={attachments}
        heightClass={editExpanded ? 'h-[40vh]' : 'h-[65vh]'}
        testid="expense-attachment-preview"
      />

      {/* T491 — Edit-Panel: collapsed shows Lieferant + Brutto + Status as a
          compact bar; expanded reveals the totals grid + status + category
          + items table. Toggle bound to localStorage so the user's
          preference persists. */}
      <div
        className="bg-white/5 border border-white/10 rounded-lg"
        data-testid="expense-edit-panel"
        data-expanded={editExpanded ? 'true' : 'false'}
      >
        <button
          type="button"
          onClick={toggleEditExpanded}
          className="w-full flex items-center justify-between gap-4 px-4 py-3 hover:bg-white/5 text-left"
          aria-expanded={editExpanded}
          data-testid="expense-edit-panel-toggle"
        >
          <div className="flex items-center gap-4 text-white/80 flex-wrap">
            <span className="text-sm text-white/50">Belegdaten</span>
            <span className="font-medium text-white">{expense.supplierName ?? '—'}</span>
            <span className="tabular-nums text-white">
              {formatCurrency(Number(expense.grossTotal))}
            </span>
            <span className="inline-block px-2 py-0.5 text-xs rounded bg-white/5 border border-white/10 text-white/80">
              {STATUS_LABELS[expense.status] ?? expense.status}
            </span>
          </div>
          <span className="text-white/50 text-lg leading-none">{editExpanded ? '⌄' : '⌃'}</span>
        </button>

        {editExpanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-white/5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white/80 pt-4">
              <div>
                <div className="text-xs text-white/50">Status</div>
                <div className="text-lg">{STATUS_LABELS[expense.status] ?? expense.status}</div>
              </div>
              <div>
                <div className="text-xs text-white/50">Netto</div>
                <div className="text-lg tabular-nums">
                  {formatCurrency(Number(expense.netTotal))}
                </div>
              </div>
              <div>
                <div className="text-xs text-white/50">MwSt</div>
                <div className="text-lg tabular-nums">
                  {formatCurrency(Number(expense.vatTotal))}
                </div>
              </div>
              <div>
                <div className="text-xs text-white/50">Brutto</div>
                <div className="text-lg font-medium text-white tabular-nums">
                  {formatCurrency(Number(expense.grossTotal))}
                </div>
              </div>
            </div>

            <Link to="/expenses/$id/edit" params={{ id }}>
              <Button variant="secondary">Positionen und Belegdaten bearbeiten</Button>
            </Link>

            {/* T490 — inline status dropdown */}
            <div className="flex flex-wrap items-center gap-4">
              <label className="text-sm text-white/50 w-24">Status</label>
              <select
                value={expense.status}
                onChange={(e) => {
                  const next = e.target.value as ExpenseStatusInput;
                  if (next !== expense.status) {
                    void onStatusChange(next);
                  }
                }}
                disabled={allowedNext.length === 0}
                data-testid="detail-status-select"
                className="bg-white/5 border border-white/10 rounded px-3 py-2 text-white"
              >
                <option value={expense.status}>
                  {STATUS_LABELS[expense.status] ?? expense.status} (aktuell)
                </option>
                {allowedNext.map((s) => (
                  <option key={s} value={s}>
                    → {STATUS_LABELS[s] ?? s}
                  </option>
                ))}
              </select>
              {allowedNext.length === 0 && (
                <span className="text-xs text-white/40">Weitere Statuswechsel nicht verfügbar</span>
              )}
            </div>

            {/* T490 — inline category picker.
                Spec 004 US5 (FR-G6-MOUNT-COVERAGE) — swapped from
                legacy <select> to CategoryComboboxWithCreate so the
                detail-page inline picker carries the same inline-
                create + case-insensitive collision-guard affordance as
                the edit form. Legacy testid `detail-category-select`
                preserved on the wrapper div for back-compat with T490
                tests; combobox itself ships `category-picker-detail-edit`. */}
            <div className="flex flex-wrap items-center gap-4" data-testid="detail-category-select">
              <label className="text-sm text-white/50 w-24">Kategorie</label>
              <div className="flex-1 min-w-[240px]" data-testid="category-picker-detail-edit">
                <CategoryComboboxWithCreate
                  value={expense.categoryId ?? categoryPick}
                  onChange={(next) => {
                    setCategoryPick(next);
                    if (next && next !== expense.categoryId) {
                      void onAssignCategory(next);
                    }
                  }}
                  testid="detail-edit"
                  placeholder="— Kategorie wählen —"
                />
              </div>
            </div>

            {/* Items table — visible when the edit panel is expanded. Read-only
                here; bulk-edit lives at `/expenses/$id_/edit`. */}
            {expense.items.length > 0 && (
              <div>
                <h2 className="text-sm text-white/50 mb-2">Positionen</h2>
                <GlassPanel variant="subtle" padding="none">
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-white/10 text-white/80">
                      <thead className="text-xs text-white/50 uppercase">
                        <tr>
                          <th className="px-2 py-2 text-left">#</th>
                          <th className="px-2 py-2 text-left">Beschreibung</th>
                          <th className="px-2 py-2 text-right">Menge</th>
                          <th className="px-2 py-2 text-right">Netto/Stück</th>
                          <th className="px-2 py-2 text-right">MwSt</th>
                          <th className="px-2 py-2 text-right">Gesamt</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {expense.items.map((it) => (
                          <tr key={it.id}>
                            <td className="px-2 py-2 text-white/50">{it.position}</td>
                            <td className="px-2 py-2">{it.description}</td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatNumber(parseFloat(it.quantity))}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatCurrency(Number(it.unitPriceNet))}
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatNumber(Number(it.vatRateAtSave))} %
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">
                              {formatCurrency(Number(it.subtotalGross))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </GlassPanel>
              </div>
            )}
          </div>
        )}
      </div>

      <AttachmentAddSection expenseId={expense.id} />
      <ExpenseAttachmentsList expenseId={expense.id} />

      <CommentsSection expenseId={expense.id} />
    </div>
  );
}

/**
 * T-G4a-fe (Phase-17, FR-G4a) — "+ Anhang hinzufügen" section.
 *
 * Simple file-input + submit. Posts multipart `files[]` to
 * /expenses/:id/attachments; on success invalidates the attachments
 * query so ExpenseAttachmentsList + ExpenseAttachmentMultiPagePreview
 * re-fetch without a page-refresh.
 */
function AttachmentAddSection({ expenseId }: { expenseId: string }) {
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();
  const tenantId = useActiveTenantId();
  const toastLocal = useToastHelpers();

  async function submit() {
    if (files.length === 0) return;
    setBusy(true);
    try {
      const form = new FormData();
      for (const f of files) form.append('files', f);
      const apiBase = (window as unknown as { VITE_API_URL?: string }).VITE_API_URL ?? '/api/v1';
      const res = await fetch(`${apiBase}/expenses/${expenseId}/attachments`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(`Upload fehlgeschlagen: ${res.status} ${msg.slice(0, 200)}`);
      }
      toastLocal.success(`${files.length} Anhang/Anhänge hinzugefügt`);
      setFiles([]);
      queryClient.invalidateQueries({
        queryKey: expenseAttachmentKeys.forExpense(tenantId, expenseId),
      });
      queryClient.invalidateQueries({ queryKey: expenseKeys.detail(tenantId, expenseId) });
    } catch (e) {
      toastLocal.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mt-4 rounded-lg border border-white/10 bg-white/5 p-3 flex items-center gap-3"
      data-testid="attachment-add-section"
    >
      <input
        type="file"
        multiple
        accept="application/pdf,image/*,text/xml,application/xml"
        onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
        data-testid="attachment-add-file-input"
        className="text-sm text-white/70"
      />
      <button
        type="button"
        disabled={busy || files.length === 0}
        onClick={submit}
        data-testid="attachment-add-submit"
        className="px-3 py-1 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-30 text-sm"
      >
        {busy ? 'Lade…' : '+ Anhang hinzufügen'}
      </button>
    </div>
  );
}

function CommentsSection({ expenseId }: { expenseId: string }) {
  const { data } = useExpenseComments(expenseId);
  const createComment = useCreateExpenseComment(expenseId);
  const toast = useToastHelpers();
  const [draft, setDraft] = useState('');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.trim()) return;
    try {
      await createComment.mutateAsync(draft);
      setDraft('');
      toast.success('Kommentar gespeichert');
    } catch (err) {
      toast.error(`Kommentar speichern fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  return (
    <div className="p-4 bg-white/5 rounded border border-white/10">
      <h2 className="text-lg font-medium text-white mb-3">Kommentare</h2>
      <div className="space-y-3 mb-4">
        {(data?.items ?? []).map((c) => (
          <div key={c.id} className="border-l-2 border-white/10 pl-3">
            <div className="flex items-center gap-2 text-xs text-white/50">
              <span
                className={
                  c.authorRoleAtSave === 'steuerberater'
                    ? 'inline-block px-1.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-200'
                    : 'inline-block px-1.5 rounded bg-white/5 border border-white/10'
                }
              >
                {c.authorRoleAtSave}
              </span>
              <span>{formatDateTimeBerlin(c.createdAt)}</span>
            </div>
            <div className="text-white/80 mt-1 whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
        {(!data || data.items.length === 0) && (
          <p className="text-white/40 text-sm">Noch keine Kommentare</p>
        )}
      </div>
      <form onSubmit={onSubmit} className="flex gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Kommentar schreiben…"
          rows={2}
          className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-sm"
        />
        <Button type="submit" disabled={createComment.isPending || !draft.trim()}>
          Senden
        </Button>
      </form>
    </div>
  );
}
